import React, { useEffect, useMemo, useState } from 'react';
import { getUiLocale } from '@/lib/dates';
import { useTranslation } from 'react-i18next';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Topbar } from '@/components/layout/Topbar';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import {
  ArrowLeft, Briefcase, Building2, ClipboardList, FileText, BarChart3,
  Loader2, Search, Mail, Phone, Calendar, Pencil, Trash2,
} from 'lucide-react';
import { toast } from 'sonner';
import { adminApi } from '@/lib/adminApi';
import { formatApiError } from '@/lib/api';
import EditAdvisorDialog from '@/components/admin/EditAdvisorDialog';
import ConfirmDeleteDialog from '@/components/shared/ConfirmDeleteDialog';

function KpiTile({ icon: Icon, label, value, testid }) {
  return (
    <Card className="p-4 shadow-card" data-testid={testid}>
      <div className="flex items-start gap-3">
        <div className="h-9 w-9 rounded-lg bg-primary/10 text-primary flex items-center justify-center shrink-0">
          <Icon className="h-5 w-5" />
        </div>
        <div className="min-w-0">
          <p className="text-xs uppercase tracking-wider font-semibold text-muted-foreground">{label}</p>
          <p className="text-2xl font-bold tabular-nums mt-0.5">{value}</p>
        </div>
      </div>
    </Card>
  );
}

/**
 * Iter 41.35 — Advisor detail (hm_admin only).
 *
 * Surfaces:
 *   - Profile header (name / email / phone / created_at)
 *   - 4 KPI tiles rolled up from per-client metrics
 *   - "Editar asignación de clientes" button → multi-select dialog
 *   - Table of assigned companies with per-client active_evaluations,
 *     pending_reports, avg_participation, last_activity + "Ver consola".
 */
export default function AdminAdvisorDetail() {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const [assignOpen, setAssignOpen] = useState(false);
  const [allCompanies, setAllCompanies] = useState([]);
  const [selected, setSelected] = useState([]);
  const [q, setQ] = useState('');
  const [saving, setSaving] = useState(false);
  // UAT Sprint 01 — findings 2 + 3
  const [editOpen, setEditOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);

  const reload = async () => {
    setLoading(true);
    try {
      const d = await adminApi.getAdvisor(id);
      setData(d);
    } catch (e) {
      toast.error(t('pages.advisors.loadFailed', { defaultValue: 'Failed to load advisor' }), {
        description: formatApiError(e),
      });
    } finally { setLoading(false); }
  };
  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [id]);

  const openAssign = async () => {
    try {
      setSelected(data?.advisor?.assigned_client_ids || []);
      const cos = await adminApi.listCompanies();
      setAllCompanies(cos || []);
      setAssignOpen(true);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const toggle = (cid) => setSelected((s) =>
    s.includes(cid) ? s.filter((x) => x !== cid) : [...s, cid]
  );

  const filteredCompanies = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return allCompanies;
    return allCompanies.filter((c) => (c.name || '').toLowerCase().includes(needle));
  }, [allCompanies, q]);

  const saveAssignments = async () => {
    setSaving(true);
    try {
      await adminApi.updateAdvisorAssignments(id, selected);
      toast.success(t('pages.advisors.assignmentsSaved', { defaultValue: 'Client book updated' }));
      setAssignOpen(false);
      await reload();
    } catch (e) {
      toast.error(t('pages.advisors.assignmentsFailed', { defaultValue: 'Could not update' }), {
        description: formatApiError(e),
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <>
        <Topbar title={t('pages.advisors.detailTitle', { defaultValue: 'Advisor detail' })} />
        <div className="p-6 lg:p-8 flex items-center justify-center text-muted-foreground" data-testid="advisor-detail-loading">
          <Loader2 className="h-5 w-5 animate-spin mr-2" /> {t('common.loading')}
        </div>
      </>
    );
  }

  if (!data?.advisor) {
    return (
      <>
        <Topbar title={t('pages.advisors.detailTitle', { defaultValue: 'Advisor detail' })} />
        <div className="p-6 lg:p-8" data-testid="advisor-detail-empty">
          <Card className="p-8 text-center text-muted-foreground">
            {t('pages.advisors.notFound', { defaultValue: 'Advisor not found.' })}
          </Card>
        </div>
      </>
    );
  }

  const { advisor, kpis, clients } = data;

  return (
    <>
      <Topbar
        title={advisor.name}
        subtitle={t('pages.advisors.detailTitle', { defaultValue: 'Advisor detail' })}
      />
      <div className="p-6 lg:p-8 space-y-6" data-testid="advisor-detail-page">
        {/* Back */}
        <Button variant="ghost" size="sm" onClick={() => navigate('/admin/advisors')}
                data-testid="advisor-back-btn" className="-ml-2">
          <ArrowLeft className="h-4 w-4 mr-1" />
          {t('pages.advisors.backToList', { defaultValue: 'Back to advisors' })}
        </Button>

        {/* Profile card */}
        <Card className="p-6 shadow-card" data-testid="advisor-profile-card">
          <div className="flex items-start justify-between gap-4">
            <div className="space-y-1">
              <h2 className="font-display text-xl font-bold">{advisor.name}</h2>
              <p className="text-sm text-muted-foreground flex items-center gap-1.5"><Mail className="h-3.5 w-3.5" /> {advisor.email}</p>
              {advisor.phone && (
                <p className="text-sm text-muted-foreground flex items-center gap-1.5"><Phone className="h-3.5 w-3.5" /> {advisor.phone}</p>
              )}
              <p className="text-xs text-muted-foreground flex items-center gap-1.5 pt-1">
                <Calendar className="h-3.5 w-3.5" />
                {t('pages.advisors.createdOn', {
                  date: advisor.created_at ? new Date(advisor.created_at).toLocaleDateString(getUiLocale()) : '—',
                  defaultValue: 'Created on {{date}}',
                })}
              </p>
              <Badge variant={advisor.status === 'active' ? 'default' : 'secondary'}
                     className={advisor.status === 'active' ? 'bg-success/15 text-success border-success/30 mt-1' : 'mt-1'}>
                {advisor.status === 'active'
                  ? t('pages.advisors.status.active',   { defaultValue: 'Active' })
                  : t('pages.advisors.status.inactive', { defaultValue: 'Inactive' })}
              </Badge>
            </div>
            <Button onClick={openAssign} data-testid="advisor-edit-assignments">
              <Briefcase className="h-4 w-4 mr-1.5" />
              {t('pages.advisors.editAssignments', { defaultValue: 'Edit client assignments' })}
            </Button>
          </div>
          {/* UAT Sprint 01 · Findings 2 + 3 — Edit + guarded delete */}
          <div className="flex flex-wrap gap-2 mt-4 pt-4 border-t border-border/60">
            <Button variant="outline" size="sm" onClick={() => setEditOpen(true)} data-testid="advisor-edit-btn">
              <Pencil className="h-4 w-4 mr-1.5" />
              {t('pages.advisors.edit', { defaultValue: 'Edit advisor' })}
            </Button>
            <Button
              variant="outline" size="sm"
              onClick={() => setDeleteOpen(true)}
              data-testid="advisor-delete-btn"
              className="text-destructive border-destructive/40 hover:bg-destructive/10"
            >
              <Trash2 className="h-4 w-4 mr-1.5" />
              {t('pages.advisors.delete', { defaultValue: 'Delete permanently' })}
            </Button>
          </div>
        </Card>

        {/* KPIs */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="advisor-kpis">
          <KpiTile icon={Building2}    label={t('pages.advisors.kpi.clients',         { defaultValue: 'Assigned clients' })}     value={kpis.client_count}             testid="advisor-kpi-clients" />
          <KpiTile icon={ClipboardList} label={t('pages.advisors.kpi.activeEvals',     { defaultValue: 'Active evaluations' })}   value={kpis.active_evaluations_total} testid="advisor-kpi-evals" />
          <KpiTile icon={FileText}     label={t('pages.advisors.kpi.pendingReports',  { defaultValue: 'Pending reports' })}      value={kpis.pending_reports_total}    testid="advisor-kpi-reports" />
          <KpiTile icon={BarChart3}    label={t('pages.advisors.kpi.avgParticipation',{ defaultValue: 'Avg. participation' })}   value={`${kpis.avg_participation_avg}%`} testid="advisor-kpi-participation" />
        </div>

        {/* Clients table */}
        <Card className="shadow-card overflow-hidden" data-testid="advisor-clients-card">
          <div className="p-4 border-b">
            <h3 className="font-semibold">{t('pages.advisors.clientsTitle', { defaultValue: 'Assigned clients' })}</h3>
          </div>
          {clients.length === 0 ? (
            <p className="py-10 text-center text-sm text-muted-foreground italic" data-testid="advisor-clients-empty">
              {t('pages.advisors.noClients', { defaultValue: 'No clients assigned yet.' })}
            </p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t('pages.advisors.col.company',        { defaultValue: 'Company' })}</TableHead>
                  <TableHead className="text-right">{t('pages.advisors.col.activeEvals',    { defaultValue: 'Active evals' })}</TableHead>
                  <TableHead className="text-right">{t('pages.advisors.col.pendingReports', { defaultValue: 'Pending reports' })}</TableHead>
                  <TableHead className="text-right">{t('pages.advisors.col.participation',  { defaultValue: 'Participation' })}</TableHead>
                  <TableHead>{t('pages.advisors.col.lastActivity',   { defaultValue: 'Last activity' })}</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {clients.map((c) => (
                  <TableRow key={c.id} data-testid={`advisor-client-row-${c.id}`}>
                    <TableCell className="font-medium">{c.name}</TableCell>
                    <TableCell className="text-right tabular-nums">{c.active_evaluations}</TableCell>
                    <TableCell className="text-right tabular-nums">{c.pending_reports}</TableCell>
                    <TableCell className="text-right tabular-nums">{c.avg_participation}%</TableCell>
                    <TableCell className="text-muted-foreground text-xs">
                      {c.last_activity ? new Date(c.last_activity).toLocaleDateString(getUiLocale()) : '—'}
                    </TableCell>
                    <TableCell>
                      <Link to={`/admin/clients/${c.id}`}
                            className="text-primary text-sm hover:underline"
                            data-testid={`advisor-client-open-${c.id}`}>
                        {t('pages.advisors.openConsole', { defaultValue: 'Open console →' })}
                      </Link>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </Card>
      </div>

      {/* Edit assignments dialog */}
      <Dialog open={assignOpen} onOpenChange={setAssignOpen}>
        <DialogContent className="max-w-xl" data-testid="advisor-assign-dialog">
          <DialogHeader>
            <DialogTitle>{t('pages.advisors.editAssignments', { defaultValue: 'Edit client assignments' })}</DialogTitle>
            <DialogDescription>
              {t('pages.advisors.editAssignmentsSubtitle', { defaultValue: "Select the companies that belong to this advisor's book." })}
            </DialogDescription>
          </DialogHeader>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input value={q} onChange={(e) => setQ(e.target.value)}
                   placeholder={t('pages.advisors.field.clientsSearchPh', { defaultValue: 'Search company…' })}
                   className="pl-9 h-9"
                   data-testid="advisor-assign-search" />
          </div>
          <div className="border rounded-lg max-h-72 overflow-auto" data-testid="advisor-assign-list">
            {filteredCompanies.map((c) => (
              <label key={c.id}
                     className="flex items-center gap-2 px-3 py-2 hover:bg-muted/40 cursor-pointer text-sm"
                     data-testid={`advisor-assign-row-${c.id}`}>
                <Checkbox checked={selected.includes(c.id)}
                          onCheckedChange={() => toggle(c.id)}
                          data-testid={`advisor-assign-cb-${c.id}`} />
                <span className="flex-1">{c.name}</span>
              </label>
            ))}
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setAssignOpen(false)} disabled={saving} data-testid="advisor-assign-cancel">
              {t('common.cancel', { defaultValue: 'Cancel' })}
            </Button>
            <Button onClick={saveAssignments} disabled={saving}
                    className="bg-gradient-primary hover:opacity-90"
                    data-testid="advisor-assign-save">
              {saving
                ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> {t('common.saving', { defaultValue: 'Saving…' })}</>
                : t('common.save', { defaultValue: 'Save' })}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* UAT Sprint 01 · Findings 2 + 3 — Edit dialog + guarded delete */}
      <EditAdvisorDialog
        open={editOpen}
        onOpenChange={setEditOpen}
        advisor={advisor}
        onUpdated={reload}
      />
      <ConfirmDeleteDialog
        open={deleteOpen}
        onOpenChange={setDeleteOpen}
        title={t('pages.advisors.deleteConfirmTitle', { defaultValue: 'Delete advisor permanently?' })}
        description={t('pages.advisors.deleteConfirmDesc', { defaultValue: 'Only inactive advisors with an empty client book can be deleted. Type the email to confirm.' })}
        expectedInput={advisor?.email || ''}
        inputPlaceholder={t('pages.advisors.deleteConfirmPh', { defaultValue: 'Type the advisor email to confirm' })}
        confirmLabel={t('pages.advisors.delete', { defaultValue: 'Delete permanently' })}
        testIdPrefix="advisor-delete"
        onConfirm={async () => {
          try {
            await adminApi.deleteAdvisor(advisor.id);
            toast.success(t('pages.advisors.deleted', { defaultValue: 'Advisor deleted' }));
            navigate('/admin/advisors');
          } catch (e) {
            const d = e?.response?.data?.detail;
            const code = typeof d === 'object' ? d?.code : null;
            if (code === 'advisor_still_active') {
              toast.error(t('pages.advisors.deleteBlockedActive', { defaultValue: 'Cannot delete — deactivate the advisor first.' }));
            } else if (code === 'advisor_book_not_empty') {
              toast.error(t('pages.advisors.deleteBlockedBook', { n: d?.client_count || 0, defaultValue: 'Cannot delete — advisor still has clients.' }));
            } else {
              toast.error(formatApiError(e));
            }
            throw e;
          }
        }}
      />
    </>
  );
}
